import type { RecentClick } from '../../../../shared/types/analytics'
import { extractHost } from '../../../../shared/lib/analytics-host'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../../../shared/lib/format'

interface RecentClicksTableProps {
  clicks: RecentClick[]
  timeZone: string
}

const COLUMNS = ['시각', '위치', '기기', '브라우저 / OS', '유입', 'IP'] as const

function formatTime(value: string, timeZone: string): string {
  return formatDateTime(value, { timeZone })
}

/** 최근 클릭 원시 로그 (IP 는 마스킹된 값만 노출) */
export default function RecentClicksTable({
  clicks,
  timeZone,
}: RecentClicksTableProps) {
  const { t } = useTranslation()
  return (
    <section className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <header className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {t('analyticsPage.recentClicks')}
        </h3>
      </header>

      {clicks.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 dark:text-gray-400">
          {t('analyticsPage.noClicks')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {COLUMNS.map(column => (
                  <th key={column} className="px-5 py-3 font-medium">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {clicks.map((click, index) => (
                <tr
                  key={`${click.clickedAt}-${index}`}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="whitespace-nowrap px-5 py-3 tabular-nums text-gray-900 dark:text-gray-100">
                    {formatTime(click.clickedAt, timeZone)}
                  </td>
                  <td className="px-5 py-3 text-gray-700 dark:text-gray-300">
                    {[click.city, click.country].filter(Boolean).join(', ') ||
                      'Unknown'}
                  </td>
                  <td className="px-5 py-3 text-gray-700 dark:text-gray-300">
                    {click.deviceType || 'Unknown'}
                  </td>
                  <td className="px-5 py-3 text-gray-700 dark:text-gray-300">
                    {[click.browser, click.os].filter(Boolean).join(' / ') ||
                      'Unknown'}
                  </td>
                  <td className="max-w-[220px] truncate px-5 py-3 text-gray-700 dark:text-gray-300">
                    {extractHost(click.referer)}
                  </td>
                  <td className="px-5 py-3 tabular-nums text-gray-500 dark:text-gray-400">
                    {click.ipMasked}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
